import { ethers } from "ethers";
import {
  SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
  buildFlatJoinSplitV2Transaction,
  buildFlatMigrateV2Transaction,
  buildFlatPrivateBridgeV2Transaction,
  buildFlatPrivateSwapV2Transaction,
  buildFlatWithdrawBatchV2Transaction,
  buildFlatWithdrawV2Transaction,
  validateFlatJoinSplitV2WithdrawBatchEntries,
  computeFlatJoinSplitV2BridgeAuxHash,
  computeFlatJoinSplitV2SwapAuxHash,
  proveFlatJoinSplitV2,
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  simulateFlatJoinSplitV2Transaction,
  type FlatJoinSplitV2BridgeData,
  type FlatJoinSplitV2Hex32,
  type FlatJoinSplitV2HexData,
  type FlatJoinSplitV2InputNoteWitness,
  type FlatJoinSplitV2OutputNoteWitness,
  type FlatJoinSplitV2ProveRequest,
  type FlatJoinSplitV2SwapData,
} from "./sdk";
import { EVM_CONFIG } from "../config";
import {
  submitBasePoolRelay,
  serializeTransaction,
} from "../utils/relayer-client";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import {
  scanFlatJoinSplitV2Tree,
  type FlatJoinSplitV2MerklePath,
} from "./merkleIndex";
import {
  assertFlatJoinSplitV2Note,
  createFlatJoinSplitV2Note,
  flatFieldHex,
} from "./notes";
import { withFlatJoinSplitV2Prover } from "./prover";
import { assertAscendingLeafIndices, selectForAmount } from "./spendSelection";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  prepareFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalDelivery,
  type FlatJoinSplitV2JournalKind,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { fail } from "./common";
import type {
  FlatJoinSplitV2BridgeParams,
  FlatJoinSplitV2OperationResult,
  FlatJoinSplitV2SwapParams,
} from "./operationTypes";
import {
  authenticateAndDedupeCandidates,
  candidateNotes,
} from "./noteSelection";
import {
  readCurrentTokenDomain,
  requireEnabled,
  requireProvider,
} from "./poolAccess";
import { proveAndSend } from "./proveAndSend";
import { selfDeliveries } from "./witness";

async function prepareAdapterSpend(params: {
  provider: ethers.Provider;
  pool: string;
  chainId: bigint;
  tokenAddress: string;
  amount: bigint;
  spendingKeys: SpendingKeys;
  notes: StoredJoinSplitNote[];
}) {
  const all = await authenticateAndDedupeCandidates({
    candidates: candidateNotes(
      params.notes,
      params.pool,
      params.chainId,
      params.tokenAddress,
    ),
    pool: params.pool,
    chainId: params.chainId,
    tokenAddress: params.tokenAddress,
    spendingKeys: params.spendingKeys,
  });
  const targetDomain = await readCurrentTokenDomain(
    params.provider,
    params.pool,
    params.tokenAddress,
  );
  const currentTreeIndex = targetDomain.treeIndex;
  const current = all.filter(({ note }) => note.treeIndex === currentTreeIndex);
  const selected = selectForAmount(current, params.amount);
  const total = selected.reduce((sum, note) => sum + note.note.value, 0n);
  const outputs: JoinSplitNote[] = [];
  if (total > params.amount) {
    outputs.push(
      await createFlatJoinSplitV2Note({
        recipientMpk: params.spendingKeys.mpk,
        chainId: params.chainId,
        poolAddress: params.pool,
        treeIndex: currentTreeIndex,
        tokenAddress: params.tokenAddress,
        tokenHash: selected[0].note.tokenHash,
        value: total - params.amount,
      }),
    );
  }
  return { selected, outputs, currentTreeIndex };
}

/** Reads the adapter the pool will actually call, so the proof binds it. */
async function readPoolAdapter(
  provider: ethers.Provider,
  pool: string,
  getter: "swapAdapter" | "bridgeAdapter",
): Promise<string> {
  const contract = new ethers.Contract(
    pool,
    [`function ${getter}() view returns (address)`],
    provider,
  );
  const adapter: string = await contract[getter]();
  if (!adapter || adapter === ethers.ZeroAddress) {
    fail(`pool has no ${getter} configured`);
  }
  return ethers.getAddress(adapter);
}

const FLAT_FIELD_MODULUS =
  21888242871839275222246405745257275088548364400416034343698204186575808495617n;

/** No B2B partner: the adapter charges the protocol fee only. */
const FLAT_ZERO_HASH = `0x${"0".repeat(64)}` as FlatJoinSplitV2Hex32;

export async function executeFlatJoinSplitV2Swap(
  params: FlatJoinSplitV2SwapParams,
): Promise<FlatJoinSplitV2OperationResult> {
  try {
    const pool = requireEnabled();
    const tokenAddress = ethers.getAddress(params.tokenAddress);
    const outputTokenAddress = ethers.getAddress(params.outputTokenAddress);
    const fee = params.fee ?? 0n;
    if (fee < 0n || fee > params.amount) fail("swap fee exceeds the amount");
    if (params.relay && fee === 0n) {
      fail("a relayed swap must carry a nonzero relayer fee");
    }
    if (params.minOutputAmount <= 0n) {
      fail("swap minimum output must be nonzero");
    }
    const { provider, chainId } = await requireProvider(params.signer);
    const adapter = await readPoolAdapter(provider, pool, "swapAdapter");
    const { selected, outputs, currentTreeIndex } = await prepareAdapterSpend({
      provider,
      pool,
      chainId,
      tokenAddress,
      amount: params.amount,
      spendingKeys: params.spendingKeys,
      notes: params.notes,
    });

    // The pool derives the re-shielded note itself from this key, the output
    // token hash it computes, and the amount it measures receiving — so only
    // the recipient key is chosen here, never the note's token or value. The
    // value is unknown until settlement, so the note cannot be built yet; the
    // key material is generated directly and the note is completed below from
    // the amount the pool reports in its PrivateSwap event.
    const outputRandom =
      BigInt(ethers.hexlify(ethers.randomBytes(32))) % FLAT_FIELD_MODULUS || 1n;
    const outputNpkValue = await computeNPK(
      params.spendingKeys.mpk,
      outputRandom,
    );
    const outputNpk =
      `0x${outputNpkValue.toString(16).padStart(64, "0")}` as FlatJoinSplitV2Hex32;

    const swapData: FlatJoinSplitV2SwapData = {
      outputToken: outputTokenAddress,
      router: ethers.getAddress(params.router),
      minOutputAmount: params.minOutputAmount,
      swapCallData: params.swapCallData,
      outputNpk,
      deadline: params.deadline,
      // Proof-committed: the adapter charges this partner's configured fee
      // split on-chain, and a submitter cannot strip or redirect it without
      // invalidating the proof. Zero hash = direct, protocol fee only.
      partnerId: params.partnerId ?? FLAT_ZERO_HASH,
    };

    params.onLog("Preparing a private swap witness", "info");
    const settled = await proveAndSend({
      ...params,
      provider,
      pool,
      chainId,
      tokenAddress,
      recipient: adapter,
      withdrawAmount: params.amount,
      fee,
      selected,
      outputs,
      kind: "swap",
      auxDataHash: computeFlatJoinSplitV2SwapAuxHash(swapData),
      swapData,
      swapOutputSeed: {
        recipientMpk: params.spendingKeys.mpk,
        random: outputRandom,
        outputNpk,
        outputTokenAddress,
      },
      deliveries: selfDeliveries(outputs, params.spendingKeys),
      relay: params.relay,
    });
    // Settlement and restart recovery use the same receipt-bound completion
    // path. The seed is already encrypted durably before any broadcast.
    const swapOutputNote = settled.outputs[settled.outputs.length - 1];
    return {
      success: true,
      txHash: settled.txHash,
      spentNotes: selected.map((note) => note.note),
      outputNotes: settled.outputs,
      changeNote: outputs.length > 0 ? settled.outputs[0] : undefined,
      journalId: settled.journalId,
      swapOutputNpk: outputNpk,
      swapOutputTreeIndex: swapOutputNote.treeIndex,
      sourceTreeIndex: Number(currentTreeIndex),
      swapOutputRandom: outputRandom,
      swapOutputTokenHash: flatFieldHex(swapOutputNote.tokenHash),
      swapOutputTokenAddress: outputTokenAddress,
    };
  } catch (error) {
    return {
      success: false,
      txHash: (error as { txHash?: string })?.txHash,
      journalId: (error as { journalId?: string })?.journalId,
      spentNotes: [],
      outputNotes: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export async function executeFlatJoinSplitV2Bridge(
  params: FlatJoinSplitV2BridgeParams,
): Promise<FlatJoinSplitV2OperationResult> {
  try {
    const pool = requireEnabled();
    const tokenAddress = ethers.getAddress(params.tokenAddress);
    const fee = params.fee ?? 0n;
    const callValue = params.callValue ?? 0n;
    if (fee < 0n || fee > params.amount) fail("bridge fee exceeds the amount");
    if (params.relay && fee === 0n) {
      fail("a relayed bridge must carry a nonzero relayer fee");
    }
    if (params.relay && callValue !== 0n) {
      // A relayed submission carries no ETH, so this would be paid from the
      // relayer's own wallet with nothing in the proof obliging repayment.
      fail(
        "a relayed bridge cannot carry a nonzero callValue; self-sign it instead",
      );
    }
    const { provider, chainId } = await requireProvider(params.signer);
    const adapter = await readPoolAdapter(provider, pool, "bridgeAdapter");
    const { selected, outputs } = await prepareAdapterSpend({
      provider,
      pool,
      chainId,
      tokenAddress,
      amount: params.amount,
      spendingKeys: params.spendingKeys,
      notes: params.notes,
    });

    const bridgeData: FlatJoinSplitV2BridgeData = {
      target: ethers.getAddress(params.target),
      callValue,
      bridgeCallData: params.bridgeCallData,
      expectedOrderId: params.expectedOrderId,
      expectation: params.expectation,
      deadline: params.deadline,
      partnerId: params.partnerId ?? FLAT_ZERO_HASH,
    };

    params.onLog("Preparing a private bridge witness", "info");
    const settled = await proveAndSend({
      ...params,
      provider,
      pool,
      chainId,
      tokenAddress,
      recipient: adapter,
      withdrawAmount: params.amount,
      fee,
      selected,
      outputs,
      kind: "bridge",
      auxDataHash: computeFlatJoinSplitV2BridgeAuxHash(bridgeData),
      bridgeData,
      deliveries: selfDeliveries(outputs, params.spendingKeys),
      relay: params.relay,
    });
    return {
      success: true,
      txHash: settled.txHash,
      spentNotes: selected.map((note) => note.note),
      outputNotes: settled.outputs,
      changeNote: settled.outputs[0],
      journalId: settled.journalId,
    };
  } catch (error) {
    return {
      success: false,
      txHash: (error as { txHash?: string })?.txHash,
      journalId: (error as { journalId?: string })?.journalId,
      spentNotes: [],
      outputNotes: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
